import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  SCHOOL_CLASSES, COLLEGE_STREAMS, COLLEGE_SEMESTERS,
  SCHOOL_SUBJECTS, COLLEGE_SUBJECTS,
  ACCEPTED_MIME, ACCEPTED_EXT_LIST, MAX_FILE_BYTES,
} from "@/lib/libraryTaxonomy";
import { GraduationCap, School, Upload, Loader2, Trash2, FileText, AlertCircle, FileCheck2, ShieldCheck, FileType2 } from "lucide-react";
import SharedNoteCard, { SharedNote } from "@/components/SharedNoteCard";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const schema = z.object({
  title: z.string().trim().min(3, "Title must be at least 3 characters").max(120),
  description: z.string().trim().max(500).optional().or(z.literal("")),
  education_level: z.enum(["school", "college"]),
  category: z.string().min(1, "Pick a class or stream"),
  semester: z.string().optional(),
  subject: z.string().min(1, "Pick a subject"),
  chapter: z.string().trim().max(120).optional().or(z.literal("")),
});

const ShareNotes = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [level, setLevel] = useState<"school" | "college">("school");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [semester, setSemester] = useState("");
  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<string>("");
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Default to College — this experience is college-focused
  useEffect(() => { setLevel("college"); }, []);

  const [myUploads, setMyUploads] = useState<SharedNote[]>([]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  const loadMine = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("shared_notes" as any)
      .select("*")
      .eq("uploader_id", user.id)
      .order("created_at", { ascending: false });
    setMyUploads((data as unknown as SharedNote[]) || []);
  };

  useEffect(() => {
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const classes = level === "school" ? SCHOOL_CLASSES : COLLEGE_STREAMS;
  const subjects = level === "school" ? SCHOOL_SUBJECTS : COLLEGE_SUBJECTS;

  const handleFile = (f: File | null) => {
    setUploadError(null);
    if (!f) { setFile(null); return; }
    if (f.size > MAX_FILE_BYTES) {
      setUploadError("File is larger than 15 MB. Please compress or split it and try again.");
      toast({ title: "File too large", description: "Max size is 15 MB.", variant: "destructive" });
      return;
    }
    if (!(f.type in ACCEPTED_MIME)) {
      setUploadError("Unsupported file type. Allowed: PDF, JPG, PNG, DOCX.");
      toast({ title: "Unsupported file", description: "Use PDF, JPG, PNG, or DOCX.", variant: "destructive" });
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError(null);
    if (!user) return;
    if (!file) {
      setUploadError("Please choose a file before uploading.");
      return;
    }
    const parsed = schema.safeParse({
      title, description, education_level: level, category,
      semester: level === "college" ? semester : undefined,
      subject, chapter,
    });
    if (!parsed.success) {
      setUploadError(parsed.error.errors[0]?.message ?? "Please review your inputs.");
      return;
    }
    if (level === "college" && !semester) {
      setUploadError("Please pick a semester.");
      return;
    }

    setSubmitting(true);
    setProgress(10);
    setStage("Preparing file…");
    try {
      const ext = ACCEPTED_MIME[file.type as keyof typeof ACCEPTED_MIME];
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;

      setProgress(35);
      setStage("Uploading to secure storage…");
      const { error: upErr } = await supabase.storage
        .from("shared-notes")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;
      setProgress(75);
      setStage("Saving details…");

      const uploaderName =
        (user.user_metadata?.full_name as string) ||
        (user.user_metadata?.name as string) ||
        user.email?.split("@")[0] ||
        "Student";

      const { error: insErr } = await supabase.from("shared_notes" as any).insert({
        uploader_id: user.id,
        uploader_name: uploaderName,
        title: title.trim(),
        description: description.trim() || null,
        education_level: level,
        category,
        semester: level === "college" ? semester : null,
        subject,
        chapter: chapter.trim() || null,
        file_path: path,
        file_type: ext,
        file_size_bytes: file.size,
      });
      if (insErr) {
        await supabase.storage.from("shared-notes").remove([path]);
        throw insErr;
      }

      setProgress(100);
      setStage("Done");
      toast({ title: "Notes shared!", description: "Thanks for helping fellow students." });
      setTitle(""); setDescription(""); setChapter(""); setFile(null);
      (document.getElementById("file-input") as HTMLInputElement | null)?.value && ((document.getElementById("file-input") as HTMLInputElement).value = "");
      loadMine();
    } catch (err: any) {
      console.error(err);
      const msg = err?.message ?? "Something went wrong while uploading. Please try again.";
      setUploadError(msg);
      toast({ title: "Upload failed", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
      setTimeout(() => { setProgress(0); setStage(""); }, 1200);
    }
  };

  const deleteMine = async (n: SharedNote) => {
    if (!confirm(`Delete "${n.title}"?`)) return;
    const { error } = await supabase.from("shared_notes" as any).delete().eq("id", n.id);
    if (error) { toast({ title: "Delete failed", variant: "destructive" }); return; }
    await supabase.storage.from("shared-notes").remove([n.file_path]);
    setMyUploads((m) => m.filter((x) => x.id !== n.id));
    toast({ title: "Deleted" });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Share Your Notes — NoteGenius"
        description="Upload your school or college notes and help thousands of students. Free, friendly, and fast."
        path="/share-notes"
      />
      <Navbar />
      <main className="container mx-auto px-4 pt-24 pb-16 max-w-5xl">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold">
            <GraduationCap className="w-3.5 h-3.5" /> Built for college students
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground flex items-center gap-2 mt-3">
            <Upload className="w-8 h-8 text-primary" /> Share your college notes
          </h1>
          <p className="text-muted-foreground mt-1">
            Help your batchmates ace their semester. Upload your own notes — no copyrighted textbooks or scanned copies of paid material.
          </p>
        </div>

        {/* File requirements panel */}
        <div className="glass rounded-2xl p-5 mb-5 grid gap-3 md:grid-cols-3">
          <div className="flex items-start gap-3">
            <FileType2 className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground">Accepted formats</p>
              <p className="text-xs text-muted-foreground">PDF, JPG, PNG, DOCX</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <FileCheck2 className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground">Max size</p>
              <p className="text-xs text-muted-foreground">15 MB per file · one file per upload</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground">Content rules</p>
              <p className="text-xs text-muted-foreground">Your own notes only. No copyrighted books or exam papers.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="glass rounded-2xl p-5 md:p-6 space-y-5">
          <div className="flex items-center gap-2 p-1 rounded-xl bg-muted/60 w-fit">
            <Button type="button" size="sm" variant={level === "college" ? "hero" : "ghost"}
              onClick={() => { setLevel("college"); setCategory(""); setSemester(""); setSubject(""); setUploadError(null); }}>
              <GraduationCap className="w-4 h-4 mr-1.5" /> College
            </Button>
            <Button type="button" size="sm" variant={level === "school" ? "hero" : "ghost"}
              onClick={() => { setLevel("school"); setCategory(""); setSemester(""); setSubject(""); setUploadError(null); }}>
              <School className="w-4 h-4 mr-1.5" /> School
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Class 10 Trigonometry full chapter notes" maxLength={120} />
            </div>
            <div>
              <Label>{level === "school" ? "Class *" : "Stream *"}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue placeholder={`Select ${level === "school" ? "class" : "stream"}`} /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {level === "college" && (
              <div>
                <Label>Semester *</Label>
                <Select value={semester} onValueChange={setSemester}>
                  <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                  <SelectContent>
                    {COLLEGE_SEMESTERS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Subject *</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="chapter">Chapter / Topic</Label>
              <Input id="chapter" value={chapter} onChange={(e) => setChapter(e.target.value)}
                placeholder="e.g. Quadratic Equations" maxLength={120} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe what these notes cover…" maxLength={500} rows={3} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="file-input">File * (PDF, JPG, PNG, DOCX · max 15 MB)</Label>
              <Input id="file-input" type="file" accept={ACCEPTED_EXT_LIST}
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)} />
              {file && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <FileCheck2 className="w-3.5 h-3.5 text-primary" />
                  Selected: <span className="font-medium text-foreground">{file.name}</span> ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          </div>

          {uploadError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Upload couldn't complete</AlertTitle>
              <AlertDescription>{uploadError}</AlertDescription>
            </Alert>
          )}

          {submitting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> {stage || "Uploading…"}
                </span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          <Button type="submit" variant="hero" size="lg" disabled={submitting} className="w-full md:w-auto">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {submitting ? "Uploading…" : "Share notes"}
          </Button>
        </form>

        <section className="mt-10">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" /> My uploads
          </h2>
          {myUploads.length === 0 ? (
            <p className="text-muted-foreground glass rounded-2xl p-6 text-center">
              You haven't shared any notes yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myUploads.map((n) => (
                <SharedNoteCard
                  key={n.id}
                  note={n}
                  onDeleted={(id) => setMyUploads((m) => m.filter((x) => x.id !== id))}
                />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ShareNotes;
